import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getOutcomeMapping, setOutcomeMapping, type OutcomeMappingRow } from '@/api/endpoints/crmOutcomeMapping';
import { outcomeLabel } from '@/services/dialer/callOutcomeLabels';
import { LoadError, errorCode } from '@/components/analytics/LoadError';

/**
 * « Correspondance des issues » (W4, audit B2-crm, Minari « Call
 * dispositions »): for each outcome of the dialer, which outcome the CRM
 * receives. Without a choice the default applies; three outcomes of the call
 * page (gatekeeper, do_not_call, ios_filter) have none, and a call that ends
 * on one fails to reach the CRM until it is mapped (SWOFT-OUT-1).
 *
 * Saving is reversible (pick the default again), so no confirmation; the
 * « Enregistré » toast only comes with the server's answer (RUI-09).
 */

const DEFAULT = '__default';

export default function OutcomeMappingPanel({ customerId }: { customerId: string | null }) {
  const { t } = useTranslation(['pages', 'dialer', 'common']);
  const qc = useQueryClient();
  const key = ['crm-outcome-mapping', customerId];
  const q = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => getOutcomeMapping(customerId!, signal),
    enabled: !!customerId,
  });
  const [draft, setDraft] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!q.data) return;
    setDraft(Object.fromEntries(q.data.outcomes.map((r) => [r.outcome, r.mapped ?? DEFAULT])));
  }, [q.data]);

  const changes = useMemo(() => {
    const out: Record<string, string | null> = {};
    for (const r of q.data?.outcomes ?? []) {
      const now = draft[r.outcome] ?? DEFAULT;
      const was = r.mapped ?? DEFAULT;
      if (now !== was) out[r.outcome] = now === DEFAULT ? null : now;
    }
    return out;
  }, [draft, q.data]);
  const dirty = Object.keys(changes).length > 0;

  const save = useMutation({
    mutationFn: () => setOutcomeMapping(customerId!, changes),
    onSuccess: (view) => {
      qc.setQueryData(key, view);
      toast.success(t('pages:crm.mapping.saved'));
    },
  });

  const crmLabel = (v: string | null) => (v ? t(`pages:crm.mapping.crm.${v}`) : t('pages:crm.mapping.notSent'));

  if (!customerId) return <p className="text-sm text-muted-foreground">{t('pages:crm.mapping.noCustomer')}</p>;
  if (q.isLoading) return <div className="h-48 animate-pulse motion-reduce:animate-none rounded-xl bg-[var(--vf-surface-3)]" />;
  if (q.isError || !q.data) return <LoadError error={q.error} onRetry={() => q.refetch()} className="bg-card rounded-xl border border-border p-5" />;

  const unmapped = q.data.outcomes.filter((r) => !r.effective).length;

  return (
    <section className="space-y-4" aria-labelledby="crm-mapping-title">
      <div>
        <h2 id="crm-mapping-title" className="text-base font-semibold text-foreground">{t('pages:crm.mapping.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('pages:crm.mapping.subtitle')}</p>
        {unmapped > 0 && (
          <p className="mt-2 text-sm" style={{ color: 'var(--vf-status-failed-fg)' }} role="status">
            {t('pages:crm.mapping.unmapped', { count: unmapped })}
          </p>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">{t('pages:crm.mapping.title')}</caption>
          <thead>
            <tr className="border-b border-border">
              <th scope="col" className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">{t('pages:crm.mapping.columns.outcome')}</th>
              <th scope="col" className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">{t('pages:crm.mapping.columns.default')}</th>
              <th scope="col" className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">{t('pages:crm.mapping.columns.sentAs')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            <tr>
              <td className="px-4 py-2.5 text-foreground">{outcomeLabel('meeting_booked', t)}</td>
              <td className="px-4 py-2.5 text-muted-foreground">{crmLabel('meeting_booked')}</td>
              <td className="px-4 py-2.5 text-xs text-muted-foreground">{t('pages:crm.mapping.meetingFixed')}</td>
            </tr>
            {q.data.outcomes.map((r: OutcomeMappingRow) => (
              <tr key={r.outcome}>
                <td className="px-4 py-2.5 text-foreground">{outcomeLabel(r.outcome, t)}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{crmLabel(r.default)}</td>
                <td className="px-4 py-2">
                  <Select value={draft[r.outcome] ?? DEFAULT} onValueChange={(v) => setDraft((d) => ({ ...d, [r.outcome]: v }))}>
                    <SelectTrigger className="h-9 w-60 max-w-full" aria-label={t('pages:crm.mapping.selectLabel', { outcome: outcomeLabel(r.outcome, t) })}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={DEFAULT}>{t('pages:crm.mapping.useDefault', { value: crmLabel(r.default) })}</SelectItem>
                      {q.data!.targets.map((target) => <SelectItem key={target} value={target}>{crmLabel(target)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
          {save.isPending ? t('common:actions.saving') : t('common:actions.save')}
        </Button>
        {save.isError && (
          <span role="alert" className="text-sm text-foreground">
            {t('pages:crm.mapping.saveError')}
            {errorCode(save.error) && <span className="ml-2 font-vf-mono text-[11px] text-muted-foreground">{errorCode(save.error)}</span>}
            <Button type="button" variant="outline" size="sm" className="ml-2" onClick={() => save.mutate()}>{t('common:actions.retry')}</Button>
          </span>
        )}
        <span className="text-xs text-muted-foreground">{t('pages:crm.mapping.appliesFromNow')}</span>
      </div>
    </section>
  );
}
